import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { ApiError } from '../lib/apiClient';

type Step = 'credentials' | 'verify' | 'enroll' | 'recovery_codes';

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const verifyTotp = useAuthStore((s) => s.verifyTotp);
  const enroll = useAuthStore((s) => s.enroll);
  const recover = useAuthStore((s) => s.recover);

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totp, setTotp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);

  const [enrollData, setEnrollData] = useState<{ qrDataUrl: string; manualKey: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await login(email.trim().toLowerCase(), password);
      if (r.next === 'enroll_required') {
        const ed = await enroll();
        setEnrollData(ed);
        setStep('enroll');
      } else {
        setStep('verify');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await verifyTotp(totp.trim());
      if (r.recoveryCodes) {
        setRecoveryCodes(r.recoveryCodes);
        setStep('recovery_codes');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Invalid code.');
    } finally {
      setBusy(false);
    }
  };

  const handleRecover = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await recover(recoveryCode.trim());
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Invalid recovery code.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all';

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Left brand panel — unchanged from the prototype */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-gray-900 to-gray-800 border-r border-gray-700">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
              <span className="text-white font-bold text-base">vD</span>
            </div>
            <div>
              <div className="text-white font-bold text-lg">vDisain PM</div>
              <div className="text-gray-400 text-sm">Project & Invoice Management</div>
            </div>
          </div>
          <div className="mt-16">
            <h1 className="text-4xl font-bold text-white leading-tight">
              From offer to<br />
              <span className="text-brand-500">invoice, tracked.</span>
            </h1>
            <p className="mt-4 text-gray-400 text-lg leading-relaxed">
              One platform for Sales and Project Managers — from contract signing to final payment.
            </p>
          </div>
        </div>
        <p className="text-gray-600 text-xs">© 2026 vDisain. Internal tool.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
              <span className="text-white font-bold">vD</span>
            </div>
            <span className="text-white font-bold text-xl">vDisain PM</span>
          </div>

          {step === 'credentials' && (
            <>
              <h2 className="text-2xl font-bold text-white">Sign in</h2>
              <p className="mt-1 text-gray-400 text-sm">Access your project workspace</p>
              <form onSubmit={handleCredentials} className="mt-8 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                    <AlertCircle size={15} className="text-red-400 shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-lg text-sm transition-colors mt-2"
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <p className="text-xs text-gray-500 text-center mt-3 flex items-center justify-center gap-1.5">
                  <ShieldCheck size={12} /> Two-factor authentication required.
                </p>
              </form>
            </>
          )}

          {step === 'enroll' && enrollData && (
            <>
              <h2 className="text-2xl font-bold text-white">Set up two-factor auth</h2>
              <p className="mt-1 text-gray-400 text-sm">
                Scan this QR with Google Authenticator, 1Password, Bitwarden, or any TOTP app.
              </p>
              <div className="mt-6 bg-white p-4 rounded-lg w-fit mx-auto">
                <img src={enrollData.qrDataUrl} alt="2FA QR code" width={192} height={192} />
              </div>
              <details className="mt-3 text-gray-400 text-xs text-center">
                <summary className="cursor-pointer">Can't scan? Show key.</summary>
                <code className="block mt-2 break-all text-gray-300 bg-gray-800 p-2 rounded">
                  {enrollData.manualKey}
                </code>
              </details>
              <form onSubmit={handleVerify} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Enter 6-digit code from app
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={totp}
                    onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    className={`${inputCls} tracking-widest text-center font-mono text-lg`}
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                    <AlertCircle size={15} className="text-red-400 shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy || totp.length !== 6}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-lg text-sm transition-colors"
                >
                  {busy ? 'Verifying…' : 'Verify & continue'}
                </button>
              </form>
            </>
          )}

          {step === 'verify' && !recoveryMode && (
            <>
              <h2 className="text-2xl font-bold text-white">Two-factor verification</h2>
              <p className="mt-1 text-gray-400 text-sm">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={handleVerify} className="mt-8 space-y-4">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className={`${inputCls} tracking-widest text-center font-mono text-lg`}
                />
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                    <AlertCircle size={15} className="text-red-400 shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy || totp.length !== 6}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-lg text-sm transition-colors"
                >
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </form>
              <button
                onClick={() => {
                  setRecoveryMode(true);
                  setError('');
                }}
                className="mt-4 text-xs text-gray-500 hover:text-gray-300 mx-auto block"
              >
                Lost your phone? Use a recovery code →
              </button>
            </>
          )}

          {step === 'verify' && recoveryMode && (
            <>
              <h2 className="text-2xl font-bold text-white">Recovery code</h2>
              <p className="mt-1 text-gray-400 text-sm">
                Enter one of the codes you saved during 2FA setup. Each code works only once.
              </p>
              <form onSubmit={handleRecover} className="mt-8 space-y-4">
                <input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="abcde-12345"
                  autoFocus
                  className={`${inputCls} font-mono`}
                />
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                    <AlertCircle size={15} className="text-red-400 shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-lg text-sm transition-colors"
                >
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </form>
              <button
                onClick={() => {
                  setRecoveryMode(false);
                  setError('');
                }}
                className="mt-4 text-xs text-gray-500 hover:text-gray-300 mx-auto block"
              >
                ← Back to authenticator code
              </button>
            </>
          )}

          {step === 'recovery_codes' && recoveryCodes && (
            <>
              <h2 className="text-2xl font-bold text-white">Save your recovery codes</h2>
              <p className="mt-1 text-gray-400 text-sm">
                Store these somewhere safe (password manager). Each code can be used once if you lose your phone.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-2 font-mono text-sm">
                {recoveryCodes.map((c) => (
                  <div key={c} className="bg-gray-800 border border-gray-700 px-3 py-2 rounded text-gray-200">
                    {c}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/')}
                className="mt-6 w-full py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg text-sm transition-colors"
              >
                I have saved them — continue
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
